/**
 * reCAPTCHA v3 configuration
 *
 * Site key is automatically loaded from globalThis.reCaptchaSiteKey (set by server-side rendering)
 * Enable/disable via REACT_APP_RECAPTCHA_ENABLED environment variable
 *
 * Usage examples:
 *
 * For analytics actions (no backend verification needed):
 * ```typescript
 * // Just call executeRecaptcha - don't send token to backend
 * executeRecaptcha(RECAPTCHA_CONFIG.ACTIONS.ADD_TO_CART);
 * ```
 *
 * For critical actions (backend verification required):
 * ```typescript
 * // Get token and send to backend for verification
 * const token = await executeRecaptchaForCriticalAction(RECAPTCHA_CONFIG.ACTIONS.LOGIN);
 * await loginAPI({ username, password, recaptchaToken: token });
 * ```
 */
export const RECAPTCHA_CONFIG = {
    // Get reCAPTCHA site key from globalThis (set by server-side rendering)
    // For localhost development, use Google's test key that works on any domain
    SITE_KEY: (() => {
        const isLocalhost =
            typeof window !== "undefined" &&
            (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

        if (isLocalhost) {
            return "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Google's test key
        }

        return globalThis.reCaptchaSiteKey || "";
    })(),

    // Enable/disable reCAPTCHA - explicitly controlled via environment variable
    // Set REACT_APP_RECAPTCHA_ENABLED=true in your .env file to enable
    // This gives you full control over when reCAPTCHA is active
    ENABLED: process.env.REACT_APP_RECAPTCHA_ENABLED === "true",

    // reCAPTCHA actions
    ACTIONS: {
        // Analytics/monitoring actions (don't need backend verification)
        ADD_TO_CART: "add_to_cart",
        VIEW_PRODUCT: "view_product",
        CART_VIEW: "cart_view",
        CHECKOUT_VIEW: "checkout_view",
        SEARCH: "search",

        // Critical actions (should send token to backend for verification)
        LOGIN: "login",
        REGISTER: "register",
        CHECKOUT: "checkout",
        SUBMIT: "submit",
        PAYMENT: "payment",
        CONTACT_FORM: "contact_form",
        PASSWORD_RESET: "password_reset",
        CHECKOUT_SUBMIT: "checkout_submit",
    } as const,

    // Actions that require backend token verification
    CRITICAL_ACTIONS: [
        "login",
        "register",
        "checkout",
        "submit",
        "payment",
        "contact_form",
        "password_reset",
        "checkout_submit",
    ] as const,
} as const;

export type RecaptchaAction =
    (typeof RECAPTCHA_CONFIG.ACTIONS)[keyof typeof RECAPTCHA_CONFIG.ACTIONS];
